/* Modal za prijavo napake: opis + posnetki zaslona.
   [PATCH napake-fe v1]

   Tri poti do slike, vse nativne (brez nove npm odvisnosti):
     1. Zajemi zaslon  — getDisplayMedia; zajame TUDI vsebino iframe (PDF),
                          česar html2canvas ne zmore. Modal se med zajemom skrije.
     2. Prilepi        — dogodek `paste` (PrintScreen -> Ctrl+V).
     3. Naloži         — navaden <input type="file">.

   Kontekst se zajame samodejno: ruta, brskalnik, viewport, izbrano podjetje
   in obroč zadnjih 20 klicev na API. */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Loader2, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useNapakeStore } from '../stores/napakeStore';
import { zadnjiKlici } from '../lib/apiLog';

const MAX_SLIK = 5;
const MAX_BYTOV = 5 * 1024 * 1024;

export function PrijavaNapakeModal() {
  const odprt = useNapakeStore((s) => s.modalOdprt);
  const zapri = useNapakeStore((s) => s.zapri);
  const nalozi = useNapakeStore((s) => s.nalozi);
  const selectedCompany = useAuthStore((s) => s.selectedCompany);
  const location = useLocation();

  const [opis, setOpis] = useState('');
  const [slike, setSlike] = useState<File[]>([]);
  const [urlji, setUrlji] = useState<string[]>([]);
  const [posiljam, setPosiljam] = useState(false);
  const [skrij, setSkrij] = useState(false);
  const vnosDatoteke = useRef<HTMLInputElement | null>(null);

  /* ob odprtju: čist list */
  useEffect(() => {
    if (odprt) {
      setOpis('');
      setSlike([]);
      setPosiljam(false);
      setSkrij(false);
    }
  }, [odprt]);

  /* predogledi — ustvari in ob spremembi pospravi za sabo */
  useEffect(() => {
    const novi = slike.map((f) => URL.createObjectURL(f));
    setUrlji(novi);
    return () => {
      novi.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch { /* tiho */ }
      });
    };
  }, [slike]);

  /* Ctrl+V — dokler je modal odprt */
  useEffect(() => {
    if (!odprt) return undefined;
    const naPrilepi = (e: any) => {
      const items = e.clipboardData && e.clipboardData.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (String(items[i].type || '').indexOf('image') === 0) {
          const f = items[i].getAsFile();
          if (f) {
            const dat = new File([f], 'prilepljeno.png', { type: f.type || 'image/png' });
            setSlike((prej) => (prej.length >= MAX_SLIK ? prej : prej.concat([dat])));
          }
        }
      }
    };
    window.addEventListener('paste', naPrilepi);
    return () => window.removeEventListener('paste', naPrilepi);
  }, [odprt]);

  /* Esc zapre */
  useEffect(() => {
    if (!odprt) return undefined;
    const naTipko = (e: any) => { if (e.key === 'Escape') zapri(); };
    window.addEventListener('keydown', naTipko);
    return () => window.removeEventListener('keydown', naTipko);
  }, [odprt, zapri]);

  const zajemiZaslon = async () => {
    const md: any = navigator.mediaDevices;
    if (!md || !md.getDisplayMedia) {
      toast.error('Brskalnik ne podpira zajema zaslona. Uporabi PrintScreen in prilepi.');
      return;
    }
    let stream: any = null;
    try {
      /* [PATCH napake-fe v1a] preferCurrentTab: brskalnik predizbere TRENUTNI
         zavihek, zato uporabniku ostane samo klik "Deli" / "Share".
         Sistemskega okna po specifikaciji NI mogoce odstraniti — edini API,
         ki bi dal zgolj potrditev (getViewportMedia), ni implementiran v
         nobenem brskalniku. Neznane clane slovarja brskalniki ignorirajo,
         zato je to varno tudi tam, kjer preferCurrentTab ni podprt. */
      stream = await md.getDisplayMedia({ video: true, audio: false, preferCurrentTab: true });
    } catch {
      return; /* uporabnik je preklical — brez sporočila */
    }
    try {
      setSkrij(true);
      await new Promise((r) => setTimeout(r, 400));
      const video: any = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      await new Promise((r) => setTimeout(r, 250));
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      video.pause();
      const blob: any = await new Promise((r) => canvas.toBlob(r, 'image/png'));
      if (blob) {
        const dat = new File([blob], 'zaslon.png', { type: 'image/png' });
        setSlike((prej) => (prej.length >= MAX_SLIK ? prej : prej.concat([dat])));
        toast.success('Zaslon zajet.');
      }
    } catch {
      toast.error('Zajem zaslona ni uspel.');
    } finally {
      try {
        stream.getTracks().forEach((t: any) => t.stop());
      } catch { /* tiho */ }
      setSkrij(false);
    }
  };

  const dodajIzVnosa = (e: any) => {
    const izbrane: File[] = Array.prototype.slice.call((e.target && e.target.files) || []);
    let skupaj = slike.slice();
    for (let i = 0; i < izbrane.length; i++) {
      const f = izbrane[i];
      if (skupaj.length >= MAX_SLIK) { toast.error('Največ ' + MAX_SLIK + ' slik.'); break; }
      if (String(f.type || '').indexOf('image') !== 0) { toast.error(f.name + ': ni slika'); continue; }
      if (f.size > MAX_BYTOV) { toast.error(f.name + ': presega 5 MB'); continue; }
      skupaj = skupaj.concat([f]);
    }
    setSlike(skupaj);
    if (vnosDatoteke.current) vnosDatoteke.current.value = '';
  };

  const odstrani = (idx: number) => {
    setSlike((prej) => prej.filter((_x, i) => i !== idx));
  };

  const poslji = async () => {
    if (!opis.trim()) { toast.error('Opiši, kaj se je zgodilo.'); return; }
    setPosiljam(true);
    const kontekst = {
      ruta: location.pathname + (location.search || ''),
      polni_url: window.location.href,
      brskalnik: navigator.userAgent,
      jezik: navigator.language,
      viewport: window.innerWidth + 'x' + window.innerHeight,
      zaslon: window.screen ? window.screen.width + 'x' + window.screen.height : '',
      podjetje: selectedCompany ? selectedCompany.name : null,
      cas_odjemalca: new Date().toISOString(),
      zadnji_klici: zadnjiKlici(),
    };
    const fd = new FormData();
    fd.append('opis', opis.trim());
    fd.append('kontekst', JSON.stringify(kontekst));
    if (selectedCompany && selectedCompany.id) {
      fd.append('company_id', String(selectedCompany.id));
    }
    slike.forEach((f) => fd.append('datoteke', f, f.name));
    try {
      const r = await api.post('/napake/prijavi', fd);
      const st = (r.data && r.data.stevilka) ? r.data.stevilka : '';
      const opoz = (r.data && r.data.opozorila) || [];
      toast.success('Prijava oddana' + (st ? ' (' + st + ')' : '') + '. Hvala.');
      if (opoz.length > 0) {
        toast('Nekaj slik ni bilo shranjenih: ' + opoz.join('; '));
      }
      zapri();
      nalozi();
    } catch (e: any) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      toast.error(typeof d === 'string' ? d : 'Prijave ni bilo mogoče oddati.');
    } finally {
      setPosiljam(false);
    }
  };

  if (!odprt) return null;

  return (
    <div className={clsx('fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 overflow-y-auto',
                         skrij && 'invisible')}>
      <div className="fixed inset-0 bg-black/50" onClick={zapri} />
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Prijavi napako</h2>
          <button onClick={zapri} title="Zapri"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Kaj se je zgodilo?
            </label>
            <textarea value={opis} onChange={(e) => setOpis(e.target.value)} rows={5}
              autoFocus
              placeholder="Kaj si delal, kaj si pričakoval in kaj se je zgodilo namesto tega."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-400" />
            <div className="mt-1 text-xs text-gray-400">
              Ruta, brskalnik in zadnji klici na strežnik se priložijo samodejno.
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={zajemiZaslon} disabled={slike.length >= MAX_SLIK}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white transition">
                Zajemi ta zavihek
              </button>
              <button onClick={() => vnosDatoteke.current && vnosDatoteke.current.click()}
                disabled={slike.length >= MAX_SLIK}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-blue-400 disabled:opacity-40 transition">
                Naloži sliko
              </button>
              <span className="text-xs text-gray-400">ali pritisni PrintScreen in prilepi s Ctrl+V</span>
              <input ref={vnosDatoteke} type="file" accept="image/*" multiple
                onChange={dodajIzVnosa} className="hidden" />
            </div>
            <div className="mt-1 text-xs text-gray-400">
              Brskalnik bo iz varnostnih razlogov vprašal za potrditev — ta zavihek je
              <b>že izbran</b>, zato klikni samo <b>Deli</b> (Share). Zajame tudi PDF v
              pregledovalniku. Če želiš posnetek celega zaslona, pritisni PrintScreen in
              prilepi s Ctrl+V — takrat ni nobenega okna. Največ {MAX_SLIK} slik, do 5 MB.
            </div>
          </div>

          {slike.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {slike.map((f, i) => (
                <div key={f.name + '|' + i}
                  className="relative border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
                  {urlji[i] && (
                    <img src={urlji[i]} alt={f.name} className="w-full h-24 object-cover" />
                  )}
                  <div className="px-2 py-1 text-[11px] text-gray-500 dark:text-gray-400 truncate">
                    {Math.round(f.size / 1024)} kB
                  </div>
                  <button onClick={() => odstrani(i)} title="Odstrani"
                    className="absolute top-1 right-1 p-1 rounded bg-white/90 dark:bg-gray-900/90 text-gray-500 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-800">
          <button onClick={zapri}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
            Prekliči
          </button>
          <button onClick={poslji} disabled={posiljam || !opis.trim()}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white transition">
            {posiljam && <Loader2 className="w-4 h-4 animate-spin" />}
            Pošlji prijavo
          </button>
        </div>
      </div>
    </div>
  );
}
